import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { FocusableMixin } from '@/internal/mixin/focusableMixin'
import { t } from '@/localization/localization'

import styles from './calendar-cell.scss?inline'

/**
 * `dap-ds-calendar-cell`
 * @summary Calendar cell represents a single cell in the calendar.
 * @element dap-ds-calendar-cell
 * @title - Calendar cell
 * @group calendar
 *
 * @slot - The content of the calendar cell.
 *
 * @csspart base - The main calendar cell container.
 * @csspart today-mark - The today mark of the calendar cell.
 *
 */
@customElement('dap-ds-calendar-cell')
export default class DapDSCalendarCell extends FocusableMixin(DdsElement) {
  /** The value of the calendar cell. */
  @property({ attribute: false }) value: Dayjs
  /** Whether the calendar cell is selected. */
  @property({ type: Boolean }) selected: boolean = false
  /** Whether the calendar cell is disabled. */
  @property({ type: Boolean }) disabledDay: boolean = false
  /** Whether the calendar cell is today. */
  @property({ type: Boolean }) today: boolean = false
  /** Whether the calendar cell is out of range. */
  @property({ type: Boolean }) outOfRange: boolean = false
  /** Whether the calendar cell is a date header. */
  @property({ type: Boolean }) header: boolean = false
  /** Whether the calendar cell is focused. */
  @property({ type: Boolean }) focused: boolean = false

  private _cachedLabel: string = ''
  private _lastLabelUpdate: number = 0

  static readonly styles = unsafeCSS(styles)

  get label(): string {
    if (this.value) {
      const currentTime = Date.now()

      // Only recalculate if state changed or more than 100ms passed (debounce rapid updates)
      if (
        this._cachedLabel === '' ||
        this._lastLabelUpdate === 0 ||
        currentTime - this._lastLabelUpdate > 100
      ) {
        // Cache expensive dayjs formatting
        const dateLabel = dayjs(this.value).format('LL, dddd')
        const todayLabel = this.today ? t('calendar.today') : ''
        const selectedLabel = this.selected ? t('selected') : ''
        this._cachedLabel = `${dateLabel} ${todayLabel} ${selectedLabel}`.trim()
        this._lastLabelUpdate = currentTime
      }

      return this._cachedLabel
    }

    return ''
  }

  public get focusElement(): HTMLElement {
    return this
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    this.setAttribute('aria-label', this.label)
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('disabledDay')) {
      this.setAttribute('aria-disabled', this.disabledDay ? 'true' : 'false')
    }

    if (_changedProperties.has('selected')) {
      if (this.role === 'button') {
        this.setAttribute('aria-selected', this.selected ? 'true' : 'false')
      }
      this.setAttribute('aria-label', this.label)
    }
  }

  getDayName(dateStr: string, locale: string) {
    const date = new Date(dateStr)
    return date.toLocaleDateString(locale, { weekday: 'long' })
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx(
          'calendar-cell',
          { 'calendar-cell--selected': this.selected },
          { 'calendar-cell--disabled': this.disabledDay },
          { 'calendar-cell--today': this.today },
          { 'calendar-cell--out-of-range': this.outOfRange },
          { 'calendar-cell--header': this.header },
          { 'calendar-cell--focused': this.focused },
        )}>
        <slot></slot>
        ${this.today
          ? html`<span
              part="today-mark"
              class="calendar-cell__today-mark"></span>`
          : nothing}
      </div>
    `
  }
}
